<template>
  <div>
    <v-dialog
      v-model="showConfirmCollapse"
      width="400px"
      scrollable
    >
      <v-card>
        <v-card-title>
          Discard Changes?
        </v-card-title>
        <v-card-text :style="{height: 'min-content'}">
          <div>Are you sure you want to close this Server?</div>
          <div>You have unsaved changes.</div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            @click="showConfirmCollapse = false"
          >
            Cancel
          </v-btn>
          <v-btn
            text
            color="error"
            @click="() => {
              $emit('click:table')
              showConfirmCollapse = false
            }"
          >
            Discard
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-hover v-slot="{ hover: hoverPrimary }">
      <v-card
        :class="[
          {'grey lighten-3': hoverPrimary && expanded},
          {'mt-6': expanded}
        ]"
        :style="{
          transition: 'margin-top 0.2s, margin-bottom 0.2s',
          'z-index': '1',
        }"
        :disabled="disabled || !!isLoading"
        @click="$emit('click')"
      >
        <v-row
          :class="[$vuetify.breakpoint.smAndDown ? 'd-flex flex-column' : 'd-grid']"
          no-gutters
        >
          <v-hover v-slot="{ hover }">
            <v-col
              cols="12"
              :class="{'grey lighten-3': hover && !expanded}"
              class="d-flex justify-space-between align-center pl-2"
              @click="() => {
                if (expanded) $emit('click:table')
                else $emit('click:server')
              }"
            >
              <div class="d-flex text-truncate">
                <server-initial-icon
                  v-bind="{server}"
                  :large="$vuetify.breakpoint.mdAndUp"
                  class="mr-3"
                />
                <div
                  :class="{'text-decoration-line-through': !server.active}"
                  class="font-weight-bold align-self-center text-truncate"
                >
                  {{ server.name }}
                </div>
              </div>
              <v-expand-transition>
                <rotator
                  v-if="!expanded"
                  state-two="45deg"
                  :rotated="hover"
                  style="transition-delay: 0.1s;"
                >
                  <v-btn
                    icon
                    @click.stop="$emit('click:server')"
                  >
                    <v-icon>mdi-cog</v-icon>
                  </v-btn>
                </rotator>
              </v-expand-transition>
            </v-col>
          </v-hover>
          <scaler
            v-if="$vuetify.breakpoint.mdAndUp"
            y-axis="0"
            :scaled="expanded"
          >
            <v-divider
              vertical
              style="border-color: grey;"
            />
          </scaler>
          <v-divider
            v-else
            class="my-2"
            style="width: 95%; margin: auto; border-color: grey;"
          />
          <v-hover v-slot="{ hover }">
            <v-col
              cols="12"
              align-self="center"
              :class="{'grey lighten-3': hover && !expanded, 'py-1 pr-2': $vuetify.breakpoint.mdAndUp, 'pb-2': $vuetify.breakpoint.smAndDown}"
              class="d-flex justify-space-between font-weight-bold"
              @click.stop="clickTable"
            >
              <div
                class="d-flex"
                style="width: 100%"
              >
                <v-expand-transition>
                  <div
                    v-if="!expanded"
                    style="width: 100%"
                    class="d-flex justify-space-around align-self-center"
                  >
                    <div
                      :class="{'text-decoration-line-through': !server.active}"
                      class="d-flex align-self-center"
                    >
                      <span v-if="$vuetify.breakpoint.smAndDown">Tables: </span>
                      <span v-if="server.eventTables">
                        {{ (server.eventTables.length || 0) + '/' + server.maxTables }}
                      </span>
                      <span v-else>
                        {{ (server.tables.length || 0) + '/' + server.maxTables }}
                      </span>
                    </div>
                    <div
                      :class="{'text-decoration-line-through': !server.active}"
                      class="align-self-center"
                    >
                      <span v-if="$vuetify.breakpoint.smAndDown">Covers: </span>
                      {{ server.covers + '/' + server.maxCovers }}
                    </div>
                    <div
                      :class="{'text-decoration-line-through': !server.active}"
                      class="align-self-center"
                    >
                      <span v-if="$vuetify.breakpoint.smAndDown">Reservations: </span>
                      {{ reservationCountsString }}
                    </div>
                  </div>
                </v-expand-transition>
              </div>
              <rotator :rotated="expanded">
                <v-btn
                  icon
                  class="py-0"
                  @click.stop="clickTable"
                >
                  <v-icon>
                    mdi-chevron-down
                  </v-icon>
                </v-btn>
              </rotator>
            </v-col>
          </v-hover>
        </v-row>
      </v-card>
    </v-hover>
    <v-expand-transition>
      <server-card-expanded-content
        v-if="expanded"
        ref="expandedContent"
        v-bind="{server, mealPeriodId, locationId, tableConfigId, date, serverAssignmentsMap, forEvent, eventId}"
        :reservation-counts="reservationCountsString"
        :class="[{'px-2': $vuetify.breakpoint.lgAndUp}]"
        @set-tables="$emit('set-tables', $event)"
        @is-loading="isLoading = $event"
        @saved="() => {
          isLoading = 0
          $emit('saved')
        }"
        @deselect-server="$emit('click:table')"
        @remove-table="$emit('remove-table', $event)"
      />
    </v-expand-transition>
  </div>
</template>

<script>
import ServerInitialIcon from '../../helperComponents/ServerInitialIcon'
import Scaler from '@/ft-vue-core/components/transformers/Scaler'
import Rotator from '@/ft-vue-core/components/transformers/Rotator'
import ServerCardExpandedContent from './ServerCardExpandedContent.vue'
export default {
  name: 'ServerCard',
  components: {ServerInitialIcon, Scaler, Rotator, ServerCardExpandedContent},
  props: {
    server: {
      type: Object,
      default () {return {}}
    },
    serverAssignmentsMap: Object,
    reservationCounts: Object,
    mealPeriodId: Number,
    locationId: Number,
    tableConfigId: Number,
    date: [String, Number],
    expanded: Boolean,
    disabled: Boolean,
    forEvent: Boolean,
    eventId: Number,
  },
  data () {
    return {
      isLoading: 0,
      showConfirmCollapse: false,
    }
  },
  computed: {
    reservationCountsString () {
      const {reservationCounts} = this.$props
      if (!reservationCounts) return '0/0'
      return `${reservationCounts.reservationCount}/${reservationCounts.coverCount}`
    }
  },
  methods: {
    /**
     * Add/Remove table assignment on `server`. Referenced from parent using $refs
     * @param {Object} table DiningLocationTable object representing table selected on canvas
     */
    selectTable (table) {
      this.$refs.expandedContent?.selectTable(table)
    },
    clickTable () {
      if (this.$refs.expandedContent?.isDirty) this.showConfirmCollapse = true
      else this.$emit('click:table')
    }
  }
}
</script>

<style scoped>
.v-card::before {
  background: none;
}
.d-grid {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
}
</style>
